import { Directive, ElementRef, Renderer2, OnInit, OnDestroy, Input, Optional, Inject } from '@angular/core';

@Directive({
  selector: '[appCustomResize]',
  standalone: true
})
export class CustomResizeDirective implements OnInit, OnDestroy {
  @Input() resizeEdges: string = 'left,right'; // 'left', 'right', 'top', 'bottom' or combination
  @Input() minWidth: number = 300;
  @Input() maxWidth: number = 800;

  private resizeHandles: HTMLElement[] = [];
  private touchStartListeners: Array<{ element: HTMLElement; handler: (e: TouchEvent) => void; cleanup: () => void }> = [];
  private isResizing = false;
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;
  private currentEdge: string = '';

  private moveListener: (() => void) | null = null;
  private endListener: (() => void) | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Optional() @Inject('ChatbotPopupService') private chatbotPopupService?: any
  ) { }

  ngOnInit(): void {
    const edges = this.resizeEdges.split(',').map(e => e.trim());
    edges.forEach(edge => this.createResizeHandle(edge));
  }

  ngOnDestroy(): void {
    this.touchStartListeners.forEach(listener => listener.cleanup());
    this.touchStartListeners = [];

    this.resizeHandles.forEach(handle => {
      this.renderer.removeChild(this.el.nativeElement, handle);
    });
    
    if (this.moveListener) this.moveListener();
    if (this.endListener) this.endListener();
  }

  private createResizeHandle(edge: string): void {
    const handle = this.renderer.createElement('div');
    this.renderer.addClass(handle, 'custom-resize-handle');
    this.renderer.addClass(handle, `resize-handle-${edge}`);

    // Style the handle
    this.renderer.setStyle(handle, 'position', 'absolute');
    
    this.renderer.setStyle(handle, 'background', 'transparent');
    this.renderer.setStyle(handle, 'transition', 'background-color 0.2s');

    if (edge === 'left' || edge === 'right') {
      this.renderer.setStyle(handle, 'width', '6px');
      this.renderer.setStyle(handle, 'height', '100%');
      this.renderer.setStyle(handle, 'top', '0');
      this.renderer.setStyle(handle, 'cursor', 'ew-resize');

      if (edge === 'left') {
        this.renderer.setStyle(handle, 'left', '0');
      } else {
        this.renderer.setStyle(handle, 'right', '0');
      }
    } else {
      this.renderer.setStyle(handle, 'height', '6px');
      this.renderer.setStyle(handle, 'width', '100%');
      this.renderer.setStyle(handle, 'left', '0');
      this.renderer.setStyle(handle, 'cursor', 'ns-resize');

      if (edge === 'top') {
        this.renderer.setStyle(handle, 'top', '0');
      } else {
        this.renderer.setStyle(handle, 'bottom', '0');
      }
    }

    // Hover effect
    this.renderer.listen(handle, 'mouseenter', () => {
      this.renderer.setStyle(handle, 'background-color', 'rgba(0, 160, 223, 0.3)');
    });

    this.renderer.listen(handle, 'mouseleave', () => {
      if (!this.isResizing) {
        this.renderer.setStyle(handle, 'background-color', 'transparent');
      }
    });

    this.renderer.listen(handle, 'mousedown', (e: MouseEvent) => {
      this.onMouseDown(e, edge);
    });

    // Add touch event support for tablet devices
    const touchStartHandler = (e: TouchEvent) => {
      this.onTouchStart(e, edge);
    };
    handle.addEventListener('touchstart', touchStartHandler, { passive: false });
    this.touchStartListeners.push({
      element: handle,
      handler: touchStartHandler,
      cleanup: () => handle.removeEventListener('touchstart', touchStartHandler)
    });

    this.renderer.appendChild(this.el.nativeElement, handle);
    this.resizeHandles.push(handle);
  }

  private onMouseDown(event: MouseEvent, edge: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.startResize(event.clientX, event.clientY, edge);
    this.setupMouseListeners();
  }

  private onTouchStart(event: TouchEvent, edge: string): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.touches.length === 0) return;

    const touch = event.touches[0];
    this.startResize(touch.clientX, touch.clientY, edge);
    this.setupTouchListeners();
  }

  private startResize(clientX: number, clientY: number, edge: string): void {
    this.isResizing = true;
    this.currentEdge = edge;
    this.startX = clientX;
    this.startY = clientY;

    const rect = this.el.nativeElement.getBoundingClientRect();
    this.startWidth = rect.width;
    this.startHeight = rect.height;

    // **FIX: Disable pointer events on all iframes to prevent event capture**
    this.disableIframePointerEvents();

    // **FIX: Add overlay to prevent iframe from capturing events**
    this.createDragOverlay();
  }

  private setupMouseListeners(): void {
    this.moveListener = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => {
      this.handleMove(e.clientX, e.clientY);
    });

    this.endListener = this.renderer.listen('document', 'mouseup', () => {
      this.endResize();
    });
  }

  private setupTouchListeners(): void {
    const touchMoveHandler = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        this.handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const touchEndHandler = () => {
      this.endResize();
    };

    document.addEventListener('touchmove', touchMoveHandler, { passive: false });
    document.addEventListener('touchend', touchEndHandler);

    this.moveListener = () => {
      document.removeEventListener('touchmove', touchMoveHandler);
    };

    this.endListener = () => {
      document.removeEventListener('touchend', touchEndHandler);
    };
  }

  private handleMove(clientX: number, clientY: number): void {
    if (!this.isResizing) return;

    const deltaX = clientX - this.startX;
    const deltaY = clientY - this.startY;
    this.updateWidthAndHeight(deltaX, deltaY);
  }

  private updateWidthAndHeight(deltaX: number, deltaY: number): void {
    if (this.currentEdge === 'left') {
      const newWidth = this.startWidth - deltaX;
      this.setWidth(newWidth);
    } else if (this.currentEdge === 'right') {
      const newWidth = this.startWidth + deltaX;
      this.setWidth(newWidth);
    } else if (this.currentEdge === 'top') {
      const newHeight = this.startHeight - deltaY;
      this.setHeight(newHeight);
    } else if (this.currentEdge === 'bottom') {
      const newHeight = this.startHeight + deltaY;
      this.setHeight(newHeight);
    }
  }

  private endResize(): void {
    if (!this.isResizing) return;

    this.isResizing = false;
    this.currentEdge = '';

    // Remove hover effect
    this.resizeHandles.forEach(handle => {
      this.renderer.setStyle(handle, 'background-color', 'transparent');
    });

    if (this.moveListener) {
      this.moveListener();
      this.moveListener = null;
    }
    if (this.endListener) {
      this.endListener();
      this.endListener = null;
    }

    // **FIX: Re-enable pointer events on iframes**
    this.enableIframePointerEvents();

    // **FIX: Remove overlay**
    this.removeDragOverlay();

  }

  private setWidth(width: number): void {
    const constrainedWidth = Math.max(this.minWidth, Math.min(this.maxWidth, width));
    this.renderer.setStyle(this.el.nativeElement, 'width', `${constrainedWidth}px`);
    localStorage.setItem('chatbot-width', `${constrainedWidth}`);

    if (this.chatbotPopupService) {
      this.chatbotPopupService.setChatbotWidth(constrainedWidth);
    }

    const viewportWidth = window.innerWidth;
    const contentWidth = viewportWidth - constrainedWidth;

    document.documentElement.style.setProperty('--chatbot-width', `${constrainedWidth}px`);
    document.documentElement.style.setProperty('--book-width', `${(contentWidth / viewportWidth) * 100}%`);
    document.documentElement.style.setProperty('--content-width-px', `${contentWidth}px`);

    // DIRECTLY update container elements only
    const mainContent = document.querySelector('.app-main-content') as HTMLElement;
    const header = document.querySelector('.app-header') as HTMLElement;

    if (mainContent) {
      mainContent.style.width = `${contentWidth}px`;
    }
    if (header) {
      header.style.width = `${contentWidth}px`;
    }

    window.dispatchEvent(new Event('resize'));
  }

  private setHeight(height: number): void {
    const minHeight = 400;
    const maxHeight = window.innerHeight - 100;
    const constrainedHeight = Math.max(minHeight, Math.min(maxHeight, height));
    this.renderer.setStyle(this.el.nativeElement, 'height', `${constrainedHeight}px`);
    localStorage.setItem('chatbot-height', `${constrainedHeight}`);
  }

  private dragOverlay: HTMLElement | null = null;

  private createDragOverlay(): void {
    this.dragOverlay = this.renderer.createElement('div');
    this.renderer.setStyle(this.dragOverlay, 'position', 'fixed');
    this.renderer.setStyle(this.dragOverlay, 'top', '0');
    this.renderer.setStyle(this.dragOverlay, 'left', '0');
    this.renderer.setStyle(this.dragOverlay, 'width', '100vw');
    this.renderer.setStyle(this.dragOverlay, 'height', '100vh');
    this.renderer.setStyle(this.dragOverlay, 'z-index', '9999');
    this.renderer.setStyle(this.dragOverlay, 'cursor', 'ew-resize'); // or ns-resize based on edge
    this.renderer.setStyle(this.dragOverlay, 'background', 'transparent');
    this.renderer.appendChild(document.body, this.dragOverlay);
  }

  private removeDragOverlay(): void {
    if (this.dragOverlay) {
      this.renderer.removeChild(document.body, this.dragOverlay);
      this.dragOverlay = null;
    }
  }

  private disableIframePointerEvents(): void {
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach(iframe => {
      this.renderer.setStyle(iframe, 'pointer-events', 'none');
      this.renderer.setAttribute(iframe, 'data-resize-disabled', 'true');
    });
  }

  private enableIframePointerEvents(): void {
    const iframes = document.querySelectorAll('iframe[data-resize-disabled]');
    iframes.forEach(iframe => {
      this.renderer.setStyle(iframe, 'pointer-events', 'auto');
      this.renderer.removeAttribute(iframe, 'data-resize-disabled');
    });
  }
}
